import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import type { User } from '@/types/User';
import type { PaginatedResponse } from '@/types/PaginatedResponse';
import api from '@/services/api'; // Axios instance with baseURL and interceptors

export const useUserStore = defineStore('userStore', () => {
    // State
    const users = ref<User[]>([]);
    const isLoading = ref<boolean>(false);
    const error = ref<string | null>(null);
    const pageSize = ref<number>(25);

    const fetchUsersForPage = async (page: number): Promise<{ users: User[]; total: number }> => {
        try {
            console.log(`fetchUsersForPage called with page: ${page}, pageSize: ${pageSize.value}`);

            const response = await api.get<PaginatedResponse<User>>('/members', {
                params: {
                    page,
                    pageSize: pageSize.value,
                },
            });

            // Check if response.data.data exists and is an array
            if (!response.data.entities) {
                console.error('response.data.data is missing or falsy:', response.data.entities);
            }

            if (!Array.isArray(response.data.entities)) {
                console.error('response.data.data is not an array:', typeof response.data.entities, response.data.entities);
            }

            return {
                users: response.data.entities as User[], // Fallback to empty array
                total: response.data.total || 0
            };
        } catch (err) {
            if (axios.isAxiosError(err)) {
                console.error('Axios Error:', {
                    url: err.config?.url,
                    method: err.config?.method,
                    status: err.response?.status,
                    data: err.response?.data
                });
                throw new Error(err.response?.data?.message || err.message);
            }
            console.error('Unexpected error:', err);
            throw new Error('An unexpected error occurred while fetching users page.');
        }
    };

    const fetchAllUsers = async () => {
        console.log('fetchAllUsers called - START');
        isLoading.value = true;
        error.value = null;

        try {
            let page = 1;
            let allFetchedUsers: User[] = [];
            let hasMore = true;

            while (hasMore) {
                console.log(`Fetching page ${page}...`);
                const response = await fetchUsersForPage(page);
                console.log('fetchUsersForPage response: ', response);

                // Defensive check
                if (!response) {
                    console.error('Response is null/undefined');
                    break;
                }

                if (!response.users) {
                    console.error('Response.users is null/undefined:', response);
                    break;
                }

                if (!Array.isArray(response.users)) {
                    console.error('Response.users is not an array:', typeof response.users, response.users);
                    break;
                }

                console.log(`Adding ${response.users.length} users from page ${page}`);
                allFetchedUsers = [...allFetchedUsers, ...response.users];

                hasMore = page * pageSize.value < response.total;
                console.log(`hasMore: ${hasMore} (${page * pageSize.value} < ${response.total})`);
                page++;
            }

            console.log('All users fetched:', allFetchedUsers.length);
            users.value = allFetchedUsers;
            return {
                users: allFetchedUsers,
                total: allFetchedUsers.length
            };
        } catch (err) {
            console.error('Error in fetchAllUsers:', err);
            error.value = err instanceof Error ? err.message : 'Failed to fetch users';
            throw err;
        } finally {
            console.log('fetchAllUsers finally block');
            isLoading.value = false;
        }
    };

    const setPageSize = async (size: number) => {
        pageSize.value = size;
    }

    return {
        // State
        users,
        isLoading,
        error,
        pageSize,
        fetchAllUsers
    };
});